import { useState, useEffect } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiFetch, API } from "@/utils/api";

// A7: el análisis llega en markdown ligero (negritas, viñetas) — antes se
// pintaban los asteriscos literales ("un score de **50/100**"). Render mínimo
// sin dependencias: **negrita** → <strong>, "- item" → "• item"; el contenedor
// conserva whitespace-pre-wrap para los saltos de línea.
function renderInsightMarkdown(text) {
  const lines = String(text).split("\n");
  return lines.map((line, i) => {
    const bulleted = line.replace(/^(\s*)[-*]\s+/, "$1• ");
    const parts = bulleted.split(/(\*\*[^*]+\*\*)/g).map((chunk, j) =>
      chunk.startsWith("**") && chunk.endsWith("**") ? (
        <strong key={j}>{chunk.slice(2, -2)}</strong>
      ) : (
        chunk
      )
    );
    return (
      <span key={i}>
        {parts}
        {i < lines.length - 1 ? "\n" : ""}
      </span>
    );
  });
}

export default function AiInsight() {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchInsight = async (refresh = false) => {
    try {
      if (refresh) setRefreshing(true);
      const url = refresh ? `${API}/ai/insight?refresh=true` : `${API}/ai/insight`;
      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setInsight(data);
      }
    } catch (e) {
      console.error("Failed to fetch AI insight:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchInsight();
  }, []);

  // Don't render if AI is not available or returned null
  if (!loading && (!insight || !insight.insight)) return null;

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-violet-500 animate-pulse" />
          <span className="text-sm text-violet-600">Generando análisis...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <Sparkles className="w-5 h-5 text-violet-500 mt-0.5 shrink-0" />
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <h3 className="text-sm font-semibold text-slate-900">Análisis del CFO</h3>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-200 text-violet-700 font-medium">IA</span>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
              {renderInsightMarkdown(insight.insight)}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchInsight(true)}
          disabled={refreshing}
          className="shrink-0 text-violet-500 hover:text-violet-700 hover:bg-violet-100"
          title="Regenerar análisis"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
        </Button>
      </div>
    </div>
  );
}
